import { computed, defineAsyncComponent, defineComponent, onMounted } from 'vue';
import { usePayoutOrderListStore } from '@/store/payout-order/payout-order-list.store';
import { storeToRefs } from 'pinia';
import {
  payoutOrderActionMap,
  payoutOrderColumnLabel,
  payoutOrderColumnsList,
} from '@/constants/payout-order/payout-order-list-column.constant';
import { paginationProperties } from '@/constants/common/constants';
import {
  PayoutOrderAction,
  PayoutOrderColumnProperties,
  PayoutOrderListItem,
  ProcessingType,
  TransactionStatus,
} from '@/models/payout-order/payout-order-list.model';
import TagListCollapsible from '@/components/tag-list-collapsible/tag-list-collapsible';
import { getTextColorByBackground } from '@/utils/get-text-color-by-background.util';
import { usePaymentStore } from '@/store/paymont/payment.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { useAdminStore } from '@/store/admin/admin.store';
import { ModerationStatus } from '@/models/payment/moderation-status.model';
import { InfoFilled, WarnTriangleFilled, WarningFilled } from '@element-plus/icons-vue';
import { ComponentSize } from '@/models/common/component-size.model';
import { usePayoutOrderList } from '@/hooks/payout-order/payout-order-list.composables';

export default defineComponent({
  name: 'PayoutOrderList',
  components: {
    TagListCollapsible,
    InfoFilled,
    WarnTriangleFilled,
    WarningFilled,
    PayoutOrderManualDialog: defineAsyncComponent(() =>
      import('@/layouts/payout-order-page/payout-order-manual-dialog/payout-order-manual-dialog')),
  },
  setup() {
    const { loadPayoutOrderList } = usePayoutOrderList();

    const { payoutOrderPermissions } = storeToRefs(usePermissionsStore());

    const adminStore = useAdminStore();
    const { adminEmail } = storeToRefs(adminStore);
    const { getAdmin } = adminStore;

    const paymentStore = usePaymentStore();
    const { currentPayoutOrder } = storeToRefs(paymentStore);
    const {
      updateProcessingModeratedTransaction,
      updateProcessingModeratedTransactionForceTake,
      setCurrentPayoutOrder,
      setManualDialogVisible,
    } = paymentStore;

    const payoutOrderListStore = usePayoutOrderListStore();
    const {
      isPayoutOrderListLoading,
      limit,
      currentPage,
      payoutOrderListCount,
      paginatedPayoutOrderList,
    } = storeToRefs(payoutOrderListStore);

    const payoutOrderTableList = computed<PayoutOrderListItem[]>(() =>
      paginatedPayoutOrderList.value.map(paginatedPayoutOrderItem => ({
        ...paginatedPayoutOrderItem,
        actionType: getPayoutOrderActionType(paginatedPayoutOrderItem),
      })),
    );

    const isGetTransactionModerationInfoAllowed = computed<boolean>(() =>
      payoutOrderPermissions.value.isGetTransactionModerationInfoAllowed);
    const isTransactionModerationInfo = computed<boolean>(() =>
      payoutOrderPermissions.value.isTransactionModerationInfo);

    const openPlayerBalance = (playerId: string, whitelabelSlug: string): void => {
      const url = `/player/${playerId}/balance?brand=${whitelabelSlug}`;
      window.open(url, '_blank');
    };

    const openPlayerProfile = (playerId: string, whitelabelSlug: string): void => {
      const url = `/player/${playerId}/info?brand=${whitelabelSlug}`;
      window.open(url, '_blank');
    };

    const takeTransaction = async ({ playerId, whitelabelSlug }: PayoutOrderListItem): Promise<void> => {
      openPlayerBalance(playerId, whitelabelSlug);
      await updateProcessingModeratedTransaction(whitelabelSlug, playerId, ModerationStatus.MODERATION);

      await loadPayoutOrderList();
    };

    const dropTransaction = async ({ playerId, whitelabelSlug }: PayoutOrderListItem): Promise<void> => {
      await updateProcessingModeratedTransaction(whitelabelSlug, playerId, ModerationStatus.WAIT);

      await loadPayoutOrderList();
    };

    const forceTakeTransaction = async ({ playerId, whitelabelSlug }: PayoutOrderListItem): Promise<void> => {
      openPlayerBalance(playerId, whitelabelSlug);
      await updateProcessingModeratedTransactionForceTake(whitelabelSlug, playerId);

      await loadPayoutOrderList();
    };

    const manualTransaction = (payoutOrder: PayoutOrderListItem): void => {
      setCurrentPayoutOrder(payoutOrder);
      setManualDialogVisible(true);
    };

    const getPayoutOrderActionType = (
      payoutOrder: PayoutOrderListItem,
    ): PayoutOrderAction | null => {
      if (payoutOrder.transactionStatus === TransactionStatus.KYC_VERIFICATION) {
        return null;
      }

      if (payoutOrder.moderateBackOfficeAdminEmail === adminEmail.value) {
        return PayoutOrderAction.DROP;
      }

      if (payoutOrder.takeableTransaction) {
        return PayoutOrderAction.TAKE;
      }

      const isManualFlow =
        payoutOrder.processingType === ProcessingType.AUTO &&
        payoutOrder.isModerationAvailable &&
        payoutOrderPermissions.value.isManualFlowAllowed;

      if (isManualFlow) {
        return PayoutOrderAction.MANUAL_FLOW;
      }

      const isForceTake =
        payoutOrder.processingType === ProcessingType.MANUAL &&
        payoutOrderPermissions.value.isForceTakeAllowed;

      if (isForceTake) {
        return PayoutOrderAction.FORCE_TAKE;
      }

      return null;
    };

    const ACTION_BUTTON_MAP:
    Record<PayoutOrderAction, ({ playerId, whitelabelSlug }: PayoutOrderListItem) => Promise<void> | void> = {
      [PayoutOrderAction.TAKE]: takeTransaction,
      [PayoutOrderAction.DROP]: dropTransaction,
      [PayoutOrderAction.MANUAL_FLOW]: manualTransaction,
      [PayoutOrderAction.FORCE_TAKE]: forceTakeTransaction,
    };

    const actionButtonClick = async (payoutOrder: PayoutOrderListItem): Promise<void> => {
      const actionType = payoutOrder.actionType;

      if (!actionType) {
        return;
      }

      await ACTION_BUTTON_MAP[actionType](payoutOrder);
    };

    const closePayoutOrderManualDialog = (): void => {
      if (!currentPayoutOrder.value) {
        return;
      }

      const { playerId, whitelabelSlug } = currentPayoutOrder.value;
      openPlayerBalance(playerId, whitelabelSlug);
      loadPayoutOrderList();
    };

    onMounted(async () => {
      await getAdmin();
    });

    return {
      isPayoutOrderListLoading,
      payoutOrderTableList,
      payoutOrderColumnsList,
      payoutOrderColumnLabel,
      PayoutOrderColumnProperties,
      limit,
      currentPage,
      paginationProperties,
      openPlayerProfile,
      getTextColorByBackground,
      payoutOrderListCount,
      payoutOrderActionMap,
      actionButtonClick,
      ComponentSize,
      closePayoutOrderManualDialog,
      isGetTransactionModerationInfoAllowed,
      isTransactionModerationInfo,
    };
  },
});
